import React, { FC, useMemo, useState, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { TopBanner, Layout, SmartVisibleChild } from '@mtsdengi/common'
import {
  getTransliteratedView,
  MetricsEvent,
  MetricsEventsEnum,
  scrollToId,
  sleep,
  useMetrics,
  window,
} from '@shared/utils'
import { CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID } from '@shared/constants'
import { EPageTabs, TPageData } from '@shared/types'
import { MobileFloatingButton } from '@shared/ui/MobileFloatingButton'
import { TabsSwitcher } from '@features/TabsSwitcher'
import { Faq } from '@features/Faq'
import { Tariffs } from '@widgets'
import { ProcessingTab } from '@widgets/ProcessingTab'
import { Requirements } from '@widgets/Requirements'
import { Premium } from '@widgets/Premium'

const DELAY_BEFORE_ACTION_MS = 300
const PAGE_TABS = {
  [EPageTabs.PROCESSING]: ProcessingTab,
  [EPageTabs.TARIFFS]: Tariffs,
  [EPageTabs.FAQ]: Faq,
  [EPageTabs.REQUIREMENTS]: Requirements,
  [EPageTabs.PREMIUM]: Premium,
} as const
const DEFAULT_HIDDEN_IDS = Object.values(CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID)

export const CreditCardsPage: FC<TPageData> = ({
  mobileFloatingButtonProps,
  layout,
  topBanner,
  tabsData,
  autoScrollQueryArgName,
  ...pageData
}) => {
  const searchParams = useSearchParams()
  const scrollToElementId = searchParams.get('scroll')
  const [tab, setTab] = useState(EPageTabs.PROCESSING)
  const sendEventData = useMetrics()
  const handleBannerButtonClick =
    ({ buttonLocation }) =>
    async () => {
      sendEventData({
        label: 'oformit_kartu',
        event: MetricsEventsEnum.CLICK,
        args: { buttonLocation },
      })

      if (tab !== EPageTabs.PROCESSING) {
        setTab(EPageTabs.PROCESSING)
        await sleep(DELAY_BEFORE_ACTION_MS)
      }

      scrollToId(CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID.GetCard)
      const firstFormInputDomElm: HTMLInputElement = document
        .getElementById(CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID.GetCard)
        .querySelector('Input')

      firstFormInputDomElm?.focus({ preventScroll: true })
    }

  useEffect(() => {
    ;(async () => {
      const urlParams = new URLSearchParams(window.location.search)

      if (urlParams.has(autoScrollQueryArgName || 'GetCard')) {
        await sleep(DELAY_BEFORE_ACTION_MS)
        scrollToId(CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID.GetCard)
      }
    })()
  }, [autoScrollQueryArgName])

  const mobileFloatingButtonMemoProps = useMemo(
    () => ({
      hiddenIds: DEFAULT_HIDDEN_IDS,
      delayBeforeAction: DELAY_BEFORE_ACTION_MS,
      ...mobileFloatingButtonProps,
    }),
    [mobileFloatingButtonProps]
  )

  const handleTabChange = (tab: EPageTabs) => {
    setTab(tab)
    sendEventData({
      label: getTransliteratedView(tabsData.find(({ id }: { id: EPageTabs }) => id === tab).label),
      event: MetricsEventsEnum.TAB_CLICK,
    })
  }

  return (
    <>
      <MetricsEvent.PageOpen conversions label="otkryl_produktovuyu_stranicu" />
      <Layout
        {...layout}
        scrollToElementId={scrollToElementId}
        footer={{ ...layout.footer, nodeId: CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID.Description }}
      >
        <TopBanner
          nodeIdMain={CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID.TopBanner}
          {...topBanner}
          button={{ ...topBanner.button, onClick: handleBannerButtonClick({ buttonLocation: 'up' }) }}
        />
        <TabsSwitcher tabs={tabsData} setTab={handleTabChange} currentTab={tab} />

        {Object.entries(PAGE_TABS).map(([key, Tab]) => (
          <SmartVisibleChild key={key} conditions={{ none: (key as EPageTabs) === tab ? 'block' : 'none' }}>
            <Tab {...pageData} />
          </SmartVisibleChild>
        ))}

        <MobileFloatingButton
          {...mobileFloatingButtonMemoProps}
          onButtonClick={handleBannerButtonClick({ buttonLocation: 'down' })}
        />
      </Layout>
    </>
  )
}
